export interface AppItem {
  id: string;
  name: string;
  url: string;
  port?: number;
  glyph?: string;
  desc?: string;
  category?: string;
  accent?: string;
}

export interface OAuthSessionInfo {
  kind: 'oauth';
  user: {
    id: string;
    email: string;
    name: string | null;
    avatarUrl: string | null;
    provider: string;
    isAdmin: boolean;
  };
  expiresAt: number;
}

export interface GuestSessionInfo {
  kind: 'guest';
  guest: { note: string | null; appIds: string[]; expiresAt: number };
  expiresAt: number;
}

export type SessionInfo = OAuthSessionInfo | GuestSessionInfo;

export interface Providers {
  google: boolean;
  github: boolean;
  dev?: boolean;
}

export interface GuestLinkSummary {
  id: string;
  url: string;
  appIds: string[];
  expiresAt: number;
  maxUses: number | null;
  usedCount: number;
  note: string | null;
  revokedAt: number | null;
  createdAt: number;
  active: boolean;
}

async function request<T>(input: string, init?: RequestInit): Promise<T> {
  const res = await fetch(input, {
    credentials: 'same-origin',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  });
  if (!res.ok) {
    let detail: unknown;
    try { detail = await res.json(); } catch { /* */ }
    const err = new Error(`HTTP ${res.status}`) as Error & { status: number; detail?: unknown };
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  return (await res.json()) as T;
}

export const api = {
  me:        () => request<SessionInfo>('/api/auth/me'),
  providers: () => request<Providers>('/api/auth/providers'),
  logout:    () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  devLogin:  () => request<{ ok: true }>('/api/auth/dev', { method: 'POST' }),

  apps: () => request<{ apps: AppItem[] }>('/api/apps'),

  guestLinks: () => request<{ links: GuestLinkSummary[] }>('/api/guest-links'),
  createGuestLink: (input: {
    appIds: string[];
    expiresAt: number;
    maxUses?: number | null;
    note?: string | null;
  }) =>
    request<{ link: GuestLinkSummary }>('/api/guest-links', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  revokeGuestLink: (id: string) =>
    request<{ ok: true }>(`/api/guest-links/${id}`, { method: 'DELETE' }),
};
